import { useQuery } from '@tanstack/react-query';
import config from 'src/config';
import type { InventoryValidationResult } from '@erp/shared-types';
import ApiError from 'src/api/ApiError';

interface ApiResponse {
  results: Record<string, InventoryValidationResult>;
}

interface UseValidateSalesOrderDraftInventoryParams {
  salesOrderDraftId: string;
  /** Enable the query (defaults to true) */
  enabled?: boolean;
}

/**
 * Hook to validate inventory for a sales order draft's line items.
 * Uses POST but is a read operation (inventory check).
 *
 * Returns a map of productVariantId -> InventoryValidationResult.
 */
function useValidateSalesOrderDraftInventory({
  salesOrderDraftId,
  enabled = true,
}: UseValidateSalesOrderDraftInventoryParams) {
  const query = useQuery<ApiResponse, ApiError>({
    queryKey: ['salesOrderDraftInventory', salesOrderDraftId],
    queryFn: async () => {
      const url = `${config.apiBaseUrl}/accounts-receivable/staged-purchase-orders/${salesOrderDraftId}/validate-inventory`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorResponse: ApiError = await response.json();
        throw new Error(errorResponse.message);
      }

      const data = await response.json();
      return data.data as ApiResponse;
    },
    enabled: enabled && !!salesOrderDraftId,
    // Don't refetch on window focus for this potentially slow query
    refetchOnWindowFocus: false,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
  });

  // React Query v5 can briefly have status='pending' with isFetching=false
  // during initialization. Use isPendingOrLoading to ensure loading state
  // is shown until data actually arrives.
  const isPendingOrLoading = query.isLoading || query.status === 'pending';

  return {
    ...query,
    isPendingOrLoading,
  };
}

export default useValidateSalesOrderDraftInventory;
